import { useEffect, useRef, useState, useCallback } from 'react';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';

export const useWebSocket = () => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const listeners = useRef(new Map());

  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) return;

    try {
      ws.current = new WebSocket(WS_URL);

      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          
          // Notify all listeners
          listeners.current.forEach((callback, type) => {
            if (data.type === type || type === 'all') {
              callback(data);
            }
          });
        } catch (err) {
          console.error('WebSocket message parse error:', err);
        }
      };

      ws.current.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        
        // Attempt to reconnect
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          setTimeout(connect, 2000 * reconnectAttempts.current);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (err) {
      console.error('WebSocket connection error:', err);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  }, []);

  const subscribeToGame = useCallback((gameId) => {
    sendMessage({ action: 'subscribe_game', game_id: gameId });
  }, [sendMessage]);

  const subscribeToTournament = useCallback((tournamentId) => {
    sendMessage({ action: 'subscribe_tournament', tournament_id: tournamentId });
  }, [sendMessage]);

  const subscribeToBetting = useCallback(() => {
    sendMessage({ action: 'subscribe_betting' });
  }, [sendMessage]);

  const addListener = useCallback((type, callback) => {
    const id = Math.random().toString(36).substr(2, 9);
    listeners.current.set(`${type}_${id}`, callback);
    return () => listeners.current.delete(`${type}_${id}`);
  }, []);

  const onGameUpdate = useCallback((callback) => {
    return addListener('game_update', callback);
  }, [addListener]);

  const onBracketUpdate = useCallback((callback) => {
    return addListener('bracket_update', callback);
  }, [addListener]);

  const onBettingUpdate = useCallback((callback) => {
    return addListener('betting_update', callback);
  }, [addListener]);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    subscribeToGame,
    subscribeToTournament,
    subscribeToBetting,
    onGameUpdate,
    onBracketUpdate,
    onBettingUpdate
  };
};

// WebSocket Provider for app-wide access
import React, { createContext, useContext } from 'react';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const websocket = useWebSocket();
  
  return (
    <WebSocketContext.Provider value={websocket}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
};
